import { cloneDeep, get, set, isNumber, isObject } from 'lodash';
import qs from 'qs';
import dayjs from 'moment';
import { ensureArray, uuid, generateMap } from '@triascloud/utils';
import { UploadService } from '@triascloud/services';
import { JSONPath } from 'jsonpath-plus';
import Vue from 'vue';
import { createModuleUrl } from '@/utils';
/** @name 参数状态 */
export const PARAM_STATUS = {
  /** @name 入参 */
  IN: 'IN',
  /** @name 出参 */
  OUT: 'OUT',
};
/** @name 入参的格式 */
export const InputFormat = {
  /** @name 不参与入参 0 */
  NEVER: 0,
  /** @name 对象 1*/
  OBJECT: 1,
  /** @name 键值 2*/
  KEYVALUE: 2,
};
/** @name POST参数的Content-Type */
export const BODY_TYPE = {
  /** @name JSON */
  JSON: 'JSON',
  /** @name form-data */
  FORM: 'form-data',
  /** @name x-www-form-urlencoded */
  X_FORM: 'x-www-form-urlencoded',
};
/** @name input_placeholder */
export const PLACEHOLDER_INPUT = '调试时填写入参';
/** @name 入参placeholder */
export const PLACEHOLDER_IN = '根据选择的DP点填写';
/** @name 出参placeholder */
export const PLACEHOLDER_OUT = '支持固定值和JsonPath解析';
/** @name 入参or出参 */
export const PARAM_TYPE = '__paramType__';
/** @name 入参的来源 */
export const STATUS = {
  /** @name 开发 */
  DEVELOPMENT: 'DEVELOPMENT',
  /** @name 平台方 */
  PLATFORM: 'PLATFORM',
  /** @name 订阅方 */
  SUBSCRIBER: 'SUBSCRIBER',
};

/** @name 文本的默认值类型 */
export const TextDefault = {
  /** @name 手动输入 */
  MANUAL: 'manual',
  /** @name 规则生成 */
  RULE: 'rule',
  /** @name 指定变量 */
  VARIABLE: 'variable',
};

/** @name 文本的规则生成的数据类型 */
export const RuleType = {
  /** @name 整数 */
  INT: 'int',
  /** @name 浮点数 */
  FLOAT: 'float',
  /** @name UUID */
  UUID: 'uuid',
};
export const DATATYPE = {
  int: 'INT32',
  float: 'FLOAT',
  double: 'DOUBLE',
  boolean: 'BOOL',
  enum: 'ENUM',
  text: 'TEXT',
  date: 'DATE',
  struct: 'STRUCT',
  array: 'ARRAY',
};
/** @name 控件类型枚举 */
export const ControlTypeEnum = {
  /** @name 分页卡 */
  Tabs: 'tabs',
  /** @name 文本框 */
  Input: 'text',
  /** @name 当前数据组 */
  CurrentDataSet: 'currentDataSet',
  /** @name 整数框 */
  Int: 'int',
  /** @name 浮点框 */
  Float: 'float',
  /** @name 双精度浮点框 */
  Double: 'double',
  /** @name 日期框 */
  Date: 'date',
  /** @name 新增数据组 */
  AddDataSet: 'addDataSet',
  /** @name 同步接口 */
  SyncInterface: 'syncInterface',
  /** @name 功能名称 */
  FnName: 'fnName',
  /** @name 功能标识 */
  FnIdentifier: 'fnIdentifier',
  /** @name 自定义模板（入参和出参） */
  CustomParam: 'customParam',
  /** @name 开关（是和否） */
  Switch: 'bool',
  /** @name 结构体 */
  Struct: 'struct',
  /** @name 数组 */
  Array: 'array',
  /** @name 枚举*/
  Enum: 'enum',
  /** @name 图片*/
  Image: 'image',
  /** @name 文件*/
  File: 'file',
  /** @name DP源*/
  DataPoint: 'dataPoint',
};
/** @name 日期的默认值类型 */
export const DateDefault = {
  /** @name 手动输入 */
  MANUAL: 'manual',
  /** @name 此刻（实时计算当前时间） */
  CURRENT: 'current',
};

export const flatFields = fields => {
  return ensureArray(fields).reduce((result, field) => {
    // 1、结构体 2、DP点 3、DP点下面的结构体
    // 获取他们的children
    if (
      field.type === ControlTypeEnum.Struct ||
      field.type === ControlTypeEnum.DataPoint ||
      field.type.toLocaleLowerCase() === DATATYPE.struct.toLocaleLowerCase()
    ) {
      return result.concat(flatFields(field.children));
    } else if (
      // 1、数组结构体 2、DP点下面的数组结构体
      (field.type === ControlTypeEnum.Array &&
        field.subType === ControlTypeEnum.Struct) ||
      (field.type.toLocaleLowerCase() === DATATYPE.array.toLocaleLowerCase() &&
        field.details?.subType?.toLocaleLowerCase() ===
          DATATYPE.struct.toLocaleLowerCase())
    ) {
      return result.concat(flatFields([].concat(...field.fields)));
    } else {
      return result.concat(field);
    }
  }, []);
};

export class ReactiveStore {
  /** @param { Record<string, T> } initData */
  constructor(initData) {
    this.data = Vue.observable(initData || {});
  }

  get(path) {
    return get(this.data, path);
  }

  set(path, value) {
    set(this.data, path, value);
  }
}

/** @name 检查组件的默认值 */
export const checkDefaultValue = param => {
  if (param.val === PLACEHOLDER_INPUT) {
    param.val = '';
  }
  if (param.val === PLACEHOLDER_OUT) {
    param.val = '';
  }
  if (param.val === PLACEHOLDER_IN) {
    param.val = '';
  }
  /** @name 布尔类型 */
  if (
    param.type === ControlTypeEnum.Switch ||
    param.type === DATATYPE.boolean.toLocaleLowerCase()
  ) {
    if (param.val === '') {
      param.val = 0;
    }
  }
  return param;
};

/** @description 递归整个数组 */
const eachList = (array, postType) => {
  if (!(Array.isArray(array) && array.length > 0)) {
    return;
  }
  array.forEach(child => {
    if (postType) {
      child.postType = postType;
    }
    if (
      child.type === ControlTypeEnum.Array &&
      child.subType === ControlTypeEnum.Struct
    ) {
      parseArrayByFields(child, child.subCount);
    }
    if (
      child.type.toLocaleLowerCase() === DATATYPE.array.toLocaleLowerCase() &&
      child.details?.subType?.toLocaleLowerCase() ===
        DATATYPE.struct.toLocaleLowerCase()
    ) {
      parseArrayByFields(child, child.details.count);
    }
    if (child.children && child.children.length > 0) {
      eachList(child.children, postType);
    }
  });
};
/** @name 【入参或出参】数组组件处理 */
export const checkArrayType = (list = [], postType = '', callback) => {
  eachList(list, postType);
  if (callback) {
    setTimeout(() => {
      callback();
    }, 20);
  }
  return list;
};

/**
 * @name 出参解析
 * @param {*} list 结构列表
 * @param {*} len 表示【结构数组】的长度
 * @param {*} parentValue 上一级节点的数据
 * @param {*} result 数据
 */
export const eachResList = async (list, len, parentValue, result) => {
  if (!(Array.isArray(list) && list.length > 0)) {
    return;
  }
  for await (let [index, item] of list.entries()) {
    if (
      item.val &&
      typeof item.val === 'string' &&
      item.val.indexOf('$.') > -1
    ) {
      let value = JSONPath({ path: item.val, json: result });
      if (item.type !== ControlTypeEnum.Array) {
        value = value[0];
      }
      if (value) {
        item.val = value;
      } else {
        if (len === -1) {
          if (isNumber(value)) {
            item.val = value;
          } else if (parentValue) {
            item.val = parentValue[item.identifier];
          }
        } else {
          /**
           * @name 获取数组的子项数据
           */
          const listLen = list.length / len;
          let rangeIdx = -1;
          rangeIdx = Math.floor(index / listLen);
          if (rangeIdx !== -1 && parentValue && rangeIdx < parentValue.length) {
            item.val = parentValue[rangeIdx][item.identifier];
          }
        }
      }
      if (
        item.type === ControlTypeEnum.Image ||
        item.type === ControlTypeEnum.File
      ) {
        await parseImageOrFile(item);
      }
    }
    if (
      item.type === ControlTypeEnum.Array &&
      item.subType === ControlTypeEnum.Struct
    ) {
      // 【结构数组】
      // 【结构数组】
      let len = 0;
      if (item.val) {
        len = item.val.length;
      }
      parseArrayByFields(item, len, true);
      if (item.fields) {
        await eachResList(
          [].concat(...item.fields),
          item.fields.length,
          item.val,
          result,
        );
      }
    } else if (
      item.type.toLocaleLowerCase() === DATATYPE.array.toLocaleLowerCase() &&
      item.details?.subType?.toLocaleLowerCase() ===
        DATATYPE.struct.toLocaleLowerCase()
    ) {
      // 【DP的结构数组】
      let len = 0;
      if (item.val) {
        len = item.val.length;
      }
      parseArrayByFields(item, len, true);
      if (item.fields) {
        await eachResList(
          [].concat(...item.fields),
          item.fields.length,
          item.val,
          result,
        );
      }
    } else if (item.children && item.children.length > 0) {
      // 【结构 or DP的结构 or DP】
      await eachResList(item.children, -1, item.val, result);
    }
  }
};

/** @name 数组组件的children的id需要唯一 */
const parseChildren = children => {
  children.forEach(v => {
    v.id = uuid();
  });
  return children;
};
/** @name 数组组件包含的children多项列表 */
export const parseArrayByFields = (item, totalCount, observable = false) => {
  let count = 0;
  const fields = [];
  while (count < totalCount) {
    fields.push(cloneDeep(parseChildren(item.children)));
    count++;
  }

  // 【数组】组件的【defaultValue】属性，拥有数据
  if (Array.isArray(item.defaultValue) && item.defaultValue.length > 0) {
    const list = item.defaultValue;
    fields.forEach((items, itemsIndex) => {
      items.forEach((field, fieldIndex) => {
        field.val = list[itemsIndex][fieldIndex];
      });
    });
  }

  if (!observable) {
    item['fields'] = fields;
  } else {
    Vue.set(item, 'fields', fields);
  }
};

const IntLen = (num = 0, len = 0) => {
  try {
    let str = num.toString();
    return parseInt(str.slice(0, len));
  } catch (error) {
    return num;
  }
};
/** @name 生成随机长度的整数和小数 */
export const createRandom = (item, random = Math.random()) => {
  let data = undefined;
  if (item.defaultType === TextDefault.RULE) {
    let len = item.ruleLength || 10;
    if (item.ruleType === RuleType.INT) {
      let t = len;
      let i = 0;
      while (i < len) {
        ++i;
        t = t + '0';
      }
      data = IntLen(parseInt(random * +t), len);
    } else if (item.ruleType === RuleType.FLOAT) {
      data = parseFloat(random.toFixed(len));
    } else {
      data = uuid();
    }
  }
  return data;
};

const customService = new UploadService('/oss/oss');
/** @name 出参解析图片和文件 */
export const parseImageOrFile = async item => {
  let val = item.val;
  if (typeof val === 'string' && val.indexOf('//') === -1) {
    val = await customService.getAuth(val);
  }
  let uid = uuid();
  item.val = [
    {
      status: 'done',
      uid: uid,
      name: uid,
      url: val,
    },
  ];
};

const keyStr = key => {
  return `{{${key}}}`;
};
/** @name 解析变量，list是对象数组 */
export const parseVariable = (value = '', list = []) => {
  const array = list.map(v => {
    return {
      key: keyStr(v.identifier),
      value: v.defaultValue,
    };
  });
  const reg = /\{\{(\w+)\}\}/g;
  const result = value.match(reg) || [];

  result.forEach(v => {
    const item = array.find(i => i.key === v);
    if (item) {
      value = value.replace(`${v}`, item.value);
    }
  });
  return value;
};

const checkFieldValue = ({ item, key, store, variableCallback, fnArg }) => {
  if (!store) return '';
  checkDefaultValue(item);

  if (item.type === ControlTypeEnum.Input) {
    /** @name 随机数生成 */
    let data = createRandom(item);
    if (data) {
      store.set(item[key], data);
    }
    if (variableCallback) {
      /** @name 变量解析 */
      if (/\{\{(\w+)\}\}/.test(store.get(item[key]))) {
        const strValue = parseVariable(
          store.get(item[key]),
          variableCallback(),
        );
        store.set(item[key], strValue);
      }
    }
  }
  // fnArg用于连接器发布，拦截【文本组件】和【时间组件】，设置后端参数【functionList】
  if (fnArg) {
    if (item.type === ControlTypeEnum.Input) {
      if (item.ruleType === RuleType.INT) {
        return 'RANDOM_INT';
      } else if (item.ruleType === RuleType.FLOAT) {
        return 'RANDOM_DOUBLE';
      } else if (item.ruleType === RuleType.UUID) {
        return 'RANDOM_UUID';
      }
    } else if (item.type === ControlTypeEnum.Date) {
      if (item.defaultType === DateDefault.CURRENT) {
        if (item.format === 'X') {
          return 'TIME_SECONDS';
        } else if (item.format === 'x') {
          return 'TIME_MILLISECOND';
        } else if (item.format === 'YYYY-MM-DDTHH:mm:ss.SSS[Z]') {
          return 'TIME_UTC_FORMAT';
        }
        return `TIME_FORMAT@${item.format}`;
      } else {
        if (item.format === 'X') {
          return 'TIME_SECONDS';
        } else if (item.format === 'x') {
          return 'TIME_MILLISECOND';
        } else if (item.format === 'YYYY-MM-DDTHH:mm:ss.SSS[Z]') {
          return 'TIME_UTC_FORMAT';
        }
        return `TIME_FORMAT@${item.format}`;
      }
    }
  }

  if (
    item.type === ControlTypeEnum.Int ||
    item.type === ControlTypeEnum.Float ||
    item.type === ControlTypeEnum.Double ||
    item.type.toLocaleLowerCase() === DATATYPE.int.toLocaleLowerCase() ||
    item.type.toLocaleLowerCase() === DATATYPE.float.toLocaleLowerCase() ||
    item.type.toLocaleLowerCase() === DATATYPE.double.toLocaleLowerCase()
  ) {
    return isNumber(-store.get(item[key])) ? Number(store.get(item[key])) : '';
  } else if (item.type === ControlTypeEnum.Image) {
    return store.get(item[key]) ? store.get(item[key])[0].ossPath : '';
  } else if (item.type === ControlTypeEnum.File) {
    return store.get(item[key]) ? store.get(item[key])[0].ossPath : '';
  } else if (
    item.type === ControlTypeEnum.Switch ||
    item.type === DATATYPE.boolean.toLocaleLowerCase()
  ) {
    return store.get(item[key]) ? 1 : 0;
  } else if (
    item.type === ControlTypeEnum.Enum ||
    item.type.toLocaleLowerCase() === DATATYPE.enum.toLocaleLowerCase()
  ) {
    return isNumber(store.get(item[key]))
      ? String(store.get(item[key]))
      : String(store.get(item[key])) === 'undefined'
      ? ''
      : String(store.get(item[key]));
  } else if (
    item.type === ControlTypeEnum.Date ||
    item.type.toLocaleLowerCase() === DATATYPE.date.toLocaleLowerCase()
  ) {
    if (item.format) {
      let val = store.get(item[key]) ? store.get(item[key]) : '';
      if (item.defaultType && item.defaultType === DateDefault.CURRENT) {
        if (val === '') {
          val = dayjs(Date.now()).format('YYYY-MM-DD HH:mm:ss');
        }
      }
      // 处理(UTC)时间格式
      // YYYY-MM-DDTHH:mm:ss.SSS[Z] 这个是旧的UTC时间格式，只做兼容显示
      // YYYY-MM-DDTHH:mm:ssZ这个是新的UTC时间格式
      if (
        item.format === 'YYYY-MM-DDTHH:mm:ssZ' ||
        item.format === 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
      ) {
        return val
          ? dayjs(val)
              .utc()
              .format()
          : val;
      } else {
        return val ? dayjs(val).format(item.format) : val;
      }
    } else {
      return store.get(item[key]) ? store.get(item[key]) : '';
    }
  } else {
    return store.get(item[key]) ? store.get(item[key]) : '';
  }
};
// TODO filter 剔除掉
const checkFieldFilter = ({
  object,
  item,
  key,
  store,
  variableCallback,
  postType,
  fnArg,
}) => {
  // 只获取对应的postType[query or body]
  if (postType) {
    if (item.postType === postType) {
      object[item.identifier] = checkFieldValue({
        item,
        key,
        store,
        variableCallback,
        fnArg,
      });
    }
    return;
  }
  object[item.identifier] = checkFieldValue({
    item,
    key,
    store,
    variableCallback,
    fnArg,
  });
};

/**
 * @name 根据接口的Method，决定参数的输入方式
 */
export const formatBody = ({
  list = [],
  store,
  key = 'uid',
  postType = '',
  variableCallback,
  fnArg = false, //
} = {}) => {
  let body = {};
  let dpBody = {};
  const eachBody = (
    list,
    body,
    key,
    store,
    variableCallback,
    postType,
    fnArg,
  ) => {
    if (!(Array.isArray(list) && list.length > 0)) {
      return;
    }
    list.forEach(item => {
      if (item.type === ControlTypeEnum.DataPoint) {
        // DP选择的某项，即只获取页面显示的组件
        const child =
          item.children && item.children.find(v => v.id === item.val);
        if (child) {
          eachBody(
            child.children,
            dpBody,
            key,
            store,
            variableCallback,
            postType,
            fnArg,
          );
        }
      } else if (
        item.type === ControlTypeEnum.Array &&
        item.subType === ControlTypeEnum.Struct
      ) {
        // 【结构数组】
        eachBody(
          [].concat(...item.fields),
          body,
          key,
          store,
          variableCallback,
          postType,
          fnArg,
        );
      } else if (
        item.type === DATATYPE.array.toLocaleLowerCase() &&
        item.details?.subType?.toLocaleLowerCase() ===
          DATATYPE.struct.toLocaleLowerCase()
      ) {
        // 【DP的结构数组】
        eachBody(
          [].concat(...item.fields),
          dpBody,
          key,
          store,
          variableCallback,
          postType,
          fnArg,
        );
      } else if (item.children && item.children.length > 0) {
        // 【结构】
        let result;
        if (
          item.type === DATATYPE.struct.toLocaleLowerCase() &&
          (item.dpType || item.isDp)
        ) {
          // 【DP结构】
          result = dpBody;
        } else {
          // 【普通结构】
          result = body;
        }
        eachBody(
          item.children,
          result,
          key,
          store,
          variableCallback,
          postType,
          fnArg,
        );
      } else {
        checkFieldFilter({
          object: body,
          item,
          key,
          store,
          variableCallback,
          postType,
          fnArg,
        });
      }
      // 2、DP 处理完成后，需要特殊处理DP的结构
      if (item.type === ControlTypeEnum.DataPoint) {
        //入参格式 不参与入参：0, 对象:1, 键值:2
        if (item.paramInFormat === InputFormat.OBJECT) {
          body = Object.assign(body, {
            [item.identifier]: `${JSON.stringify(dpBody)}`,
          });
        } else if (item.paramInFormat === InputFormat.KEYVALUE) {
          body = Object.assign(body, { ...dpBody });
        }

        // paramInTag: 'identifier', //入参标识
        if (item.paramInTag) {
          const result = item.children.find(i => i.id === item.val);
          let _identifier = '';
          if (result) {
            _identifier = result.identifier;
          }
          body = Object.assign(body, {
            [item.paramInTag]: _identifier,
          });
        }
        dpBody = {};
      }
    });
  };
  eachBody(list, body, key, store, variableCallback, postType, fnArg);
  return body;
};

/** @name url自带了path入参 */
export const parseUrlPath = (url = '', body) => {
  if (url.includes('{')) {
    let identifier = '';
    Array.from(url.matchAll(/\{(\w+)\}/g), v => v[1]).forEach(v => {
      identifier += `/${body[v]}`;
    });
    const urlStr = url.split('{')[0].slice(0, -1);
    url = `${urlStr}${identifier}`;
  }
  return url;
};
/** @name url的query入参 */
export const parseUrlQuery = (url = '', method, body) => {
  let urlStr = '';
  Object.keys(body).forEach(v => {
    if (isObject(body[v])) {
      const obj = {};
      obj[v] = body[v];
      urlStr += encodeURI(
        qs.stringify(obj, { allowDots: true, encode: false }),
      );
    } else {
      urlStr += `&${v}=${body[v]}`;
    }
  });
  if (url.includes('?')) {
    url = url + urlStr;
  } else {
    let str = urlStr.charAt(0) === '&' ? urlStr.slice(1) : urlStr;
    url = url + (urlStr ? '?' + str : '');
  }
  return url;
};
const mapHeader = (list = []) => {
  const headers = {};
  list.forEach(v => {
    headers[v.identifier] = v.val;
  });
  return headers;
};
export const headerOptions = (list = [], otherHeader = {}) => {
  let headers = mapHeader(list);
  if (otherHeader) {
    headers = Object.assign(otherHeader, headers);
  }
  return Object.keys(headers).length ? JSON.stringify(headers) : '';
};

/** @name HTTP请求实体 */
export const fetchBody = (Method, formtType, list, otherBody, store) => {
  if (Method === 'GET') return null;
  const data = formatBody({ list, store, postType: 'body', key: 'id' });
  if (formtType === BODY_TYPE.JSON) {
    return Object.assign(otherBody ? otherBody : {}, data);
  } else if (formtType === BODY_TYPE.FORM) {
    let formData = new FormData();
    if (otherBody) {
      Object.keys(otherBody).forEach(v => {
        formData.append(v, data[v]);
      });
    }
    Object.keys(data).forEach(v => {
      formData.append(v, data[v]);
    });
    return formData;
  }
};

/** @name 解析Header的变量 */
export const parseHeaderVariable = (headerString = '', variables = []) => {
  const headers = JSON.parse(headerString);
  headers.forEach(v => {
    v.val = parseVariable(v.val, variables);
  });
  return headers;
};

/** @name 前置接口参数解析 */
export const prefixAPiFormat = (res, variables = []) => {
  const prefix = {
    queryParam: res.requestUrlParam
      ? checkArrayType(JSON.parse(res.requestUrlParam))
      : [],
    bodyParam: res.requestBodyParam
      ? checkArrayType(JSON.parse(res.requestBodyParam).data)
      : [],
    HeaderParam: res.requestHeaderParam
      ? parseHeaderVariable(res.requestHeaderParam, [])
      : [],
    url: res.beforeUrl,
  };

  const paramList = () => {
    let list = [];
    if (prefix.bodyParam) {
      list = list.concat(prefix.bodyParam);
    }
    if (prefix.queryParam) {
      list = list.concat(prefix.queryParam);
    }
    return list.filter(v => v.pkId === v.id);
  };

  let data = generateMap(flatFields(paramList()), 'id', item => {
    checkDefaultValue(item);
    return item.val;
  });
  const store = new ReactiveStore(data);

  let body = formatBody({
    list: paramList(),
    store,
    key: 'id',
  });
  const header = mapHeader(prefix.HeaderParam);
  body = Object.assign(body, header);

  const fnList = (array = []) => {
    let list = [];
    if (array) {
      list = list.concat(array);
    }
    return list.filter(v => v.pkId === v.id);
  };

  const prefixFnQueryData = formatBody({
    list: fnList(prefix.queryParam),
    store,
    key: 'id',
    variableCallback: () => variables,
    fnArg: true,
  });

  const prefixFnBodyData = formatBody({
    list: fnList(prefix.bodyParam),
    store,
    key: 'id',
    variableCallback: () => variables,
    fnArg: true,
  });

  const url = createModuleUrl('api', 'https:') + '/iot-platform' + prefix.url;

  return {
    prefixUrl: url,
    prefixData: body,
    prefixFnQueryData,
    prefixFnBodyData,
  };
};

/** @name 根据identifier查找field【组件】的value */
const findIdentifier = (list, target) => {
  const result = [];
  list.forEach(v => {
    if (
      v.type === ControlTypeEnum.Array &&
      v.subType === ControlTypeEnum.Struct
    ) {
      v.fields.forEach(v => {
        v.forEach(field => {
          if (field.identifier === target.identifier) {
            result.push(field.val);
          }
        });
      });
    }
  });
  return result;
};
/** @name 出参规则提示语校验 */
export const promptValidate = (array, store, list) => {
  const msgList = [];
  array.forEach(v => {
    let resCount = 0;
    v.conditions.forEach(field => {
      const valueList = findIdentifier(list, field);
      if (valueList.length) {
        let count = 0;
        valueList.forEach(value => {
          count += checkOutValue(value, field);
        });
        if (count > 0 && valueList.length === count) {
          resCount++;
        }
      } else {
        // 根据identifier查找
        const value = store.get(field.fieldId);
        let count = checkOutValue(value, field);
        if (count > 0) {
          resCount++;
        }
      }
    });
    if (v.logic === 'AND' && v.conditions.length === resCount) {
      msgList.push(v.prompt);
    }
    if (v.logic === 'OR' && resCount > 0) {
      msgList.push(v.prompt);
    }
  });
  return msgList;
};

export const CompareMap = {
  /** @name 等于 */
  eq: 'EQ',
  /** @name 不等于 */
  notEq: 'NOTEQ',
  /** @name 包含 */
  contains: 'CONTAINS',
  /** @name 不包含 */
  notContains: 'NOTCONTAINS',
  /** @name 为空 */
  null: 'NULL',
  /** @name 不为空 */
  notNull: 'NOTNULL',
  /** @name 小于 */
  lt: 'LT',
  /** @name 小于等于 */
  le: 'LE',
  /** @name 大于 */
  gt: 'GT',
  /** @name 大于等于 */
  ge: 'GE',
};
/** @name 出参规则校验 */
const checkOutValue = (value, field) => {
  let resCount = 0;
  if (value) {
    if (field.algorithm === CompareMap.eq) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase()
      ) {
        if (+field.value === +value) {
          resCount++;
        }
      } else if (
        field.fieldType === ControlTypeEnum.Enum ||
        field.fieldType === ControlTypeEnum.Switch
      ) {
        if (field.value === value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Input) {
        const eqComplex = type => JSON.stringify(type.replace(/\s/g, ''));
        if (eqComplex(field.value) === eqComplex(value)) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Date) {
        if (dayjs(field.value).format('x') === dayjs(value).format('x')) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Array) {
        if (
          JSON.stringify(field.value) ===
          JSON.stringify(value.replace(/\s/g, ''))
        ) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.notEq) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase()
      ) {
        if (+field.value !== +value) {
          resCount++;
        }
      } else if (
        field.fieldType === ControlTypeEnum.Enum ||
        field.fieldType === ControlTypeEnum.Switch
      ) {
        if (field.value !== value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Input) {
        const eqComplex = type => JSON.stringify(type.replace(/\s/g, ''));
        if (eqComplex(field.value) !== eqComplex(value)) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Date) {
        if (dayjs(field.value).format('x') !== dayjs(value).format('x')) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Array) {
        if (
          JSON.stringify(field.value) !==
          JSON.stringify(value.replace(/\s/g, ''))
        ) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.lt) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase()
      ) {
        if (value < field.value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Date) {
        if (dayjs(value).format('x') < dayjs(field.value).format('x')) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.le) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase()
      ) {
        if (value <= field.value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Date) {
        if (dayjs(value).format('x') <= dayjs(field.value).format('x')) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.gt) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase()
      ) {
        if (value > field.value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Date) {
        if (dayjs(value).format('x') > dayjs(field.value).format('x')) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.ge) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase()
      ) {
        if (value >= field.value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Date) {
        if (dayjs(value).format('x') >= dayjs(field.value).format('x')) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.null) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase() ||
        field.fieldType === ControlTypeEnum.Date ||
        field.fieldType === ControlTypeEnum.Input ||
        field.fieldType === ControlTypeEnum.Image
      ) {
        if (!value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Array) {
        if (Array.isArray(value).length === 0) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.notNull) {
      if (
        field.fieldType === ControlTypeEnum.Int ||
        field.fieldType === ControlTypeEnum.Float ||
        field.fieldType === ControlTypeEnum.Double ||
        field.fieldType === DATATYPE.int.toLocaleLowerCase() ||
        field.fieldType === ControlTypeEnum.Date ||
        field.fieldType === ControlTypeEnum.Input ||
        field.fieldType === ControlTypeEnum.Image
      ) {
        if (value) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Array) {
        if (Array.isArray(value).length > 0) {
          resCount++;
        }
      }
    } else if (field.algorithm === CompareMap.contains) {
      if (
        field.fieldType === ControlTypeEnum.Input ||
        field.fieldType === ControlTypeEnum.Enum
      ) {
        if (value.includes(field.value)) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Array) {
        try {
          const arr = Array.from(JSON.parse(field.value));
          let fCount = 0;
          arr.forEach(i => {
            let fIdx = Array.from(value).find(v => v === i);
            if (fIdx !== -1) {
              fCount++;
            }
          });
          if (fCount === arr.length) {
            resCount++;
          }
        } catch (error) {
          return;
        }
      }
    } else if (field.algorithm === CompareMap.notContains) {
      if (
        field.fieldType === ControlTypeEnum.Input ||
        field.fieldType === ControlTypeEnum.Enum
      ) {
        if (!value.includes(field.value)) {
          resCount++;
        }
      } else if (field.fieldType === ControlTypeEnum.Array) {
        try {
          const arr = Array.from(JSON.parse(field.value));
          let fCount = 0;
          arr.forEach(i => {
            let fIdx = Array.from(value).find(v => v === i);
            if (!fIdx) {
              fCount++;
            }
          });
          if (fCount > 0) {
            resCount++;
          }
        } catch (error) {
          return;
        }
      }
    }
  }
  return resCount;
};

/** @name 删除parent属性 */
export const eachDelParent = list => {
  if (!(Array.isArray(list) && list.length > 0)) {
    return false;
  }
  for (let child of list) {
    if (child.parent) {
      delete child.parent;
    }
    if (child.children && child.children.length > 0) {
      eachDelParent(child.children);
    }
  }
};
